import { useEffect, useState } from "react";
import {
  CASH_ORDER,
  CASH_VALUES,
  cashCounts,
  cashLabel,
  countsAsOnes,
  countsTotal,
  formatCents,
  maxOf,
  recount,
  type CashCounts,
} from "../lib/money";
import { CashFill } from "./Cash";

/* The same money, counted your way.
 *
 * A jar on the shelf shows the FEWEST notes and coins, because that is what
 * fits and what a glance can count. This is the question that comes next, and
 * it is the one a child actually asks: my allowance came as five one-dollar
 * bills, so where did they go? Sliding the $1 row up puts them back, and the
 * fives and quarters rearrange themselves around it.
 *
 * The total never moves. That is the whole lesson -- $7.50 has many shapes and
 * exactly one value -- and it is why nothing here touches the API: no money is
 * moving, only the way it is being counted.
 */

// Ten rows would be a wall. Denominations bigger than the balance are
// pointless (you cannot make $7.50 out of fifties) and the smallest coins are
// rarely interesting, so the rows are the largest few that can take part.
const MAX_ROWS = 6;

export function CountItOut({ cents, hue }: { cents: number; hue: string }) {
  const [counts, setCounts] = useState<CashCounts>(() => cashCounts(cents));

  // A payout or a jar move lands while the panel is open; the counting has to
  // start again from the new amount rather than keep insisting on the old one.
  useEffect(() => setCounts(cashCounts(cents)), [cents]);

  if (cents <= 0) return null;

  const rows = CASH_ORDER.filter((kind) => CASH_VALUES[kind] <= cents).slice(0, MAX_ROWS);
  const fewest = cashCounts(cents);
  const ones = countsAsOnes(cents);
  const is = (other: CashCounts) =>
    CASH_ORDER.every((k) => (counts[k] ?? 0) === (other[k] ?? 0));

  return (
    <div style={{ display: "grid", gap: 12, marginBottom: 16 }}>
      <div
        style={{
          display: "grid",
          gap: 10,
          padding: "12px 12px 14px",
          background: "var(--paper)",
          border: "2px solid var(--paper-edge)",
          borderRadius: "var(--r-md)",
        }}
      >
        <p style={{ margin: 0, fontWeight: 600, fontSize: 15 }}>
          Count it out your way
        </p>

        {/* The picture answers the sliders, so a child who cannot yet read the
            numbers still sees what they just did. */}
        <div style={{ display: "flex", justifyContent: "center", padding: "2px 0" }}>
          <CashFill cents={cents} counts={counts} />
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          {rows.map((kind) => {
            const max = maxOf(cents, kind);
            const value = counts[kind] ?? 0;
            return (
              <label
                key={kind}
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                <span
                  className="money"
                  style={{
                    width: 42,
                    flex: "0 0 auto",
                    fontWeight: 700,
                    fontSize: 14,
                    textAlign: "right",
                  }}
                >
                  {cashLabel(kind)}
                </span>
                <input
                  type="range"
                  min={0}
                  max={max}
                  step={1}
                  value={value}
                  aria-label={`How many ${cashLabel(kind)}`}
                  onChange={(e) =>
                    setCounts((c) => recount(cents, c, kind, Number(e.target.value)))
                  }
                  style={{ flex: 1, minWidth: 0, accentColor: hue }}
                />
                <span
                  className="money"
                  style={{
                    width: 26,
                    flex: "0 0 auto",
                    fontWeight: 700,
                    fontSize: 15,
                    textAlign: "right",
                    color: value > 0 ? "var(--ink)" : "var(--ink-3)",
                  }}
                >
                  {value}
                </span>
              </label>
            );
          })}
        </div>

        {/* The promise, kept in front of them. countsTotal is recomputed from
            the sliders rather than echoing `cents`, so if the arithmetic ever
            did drift this line would be the thing that said so. */}
        <p
          role="status"
          style={{
            margin: 0,
            textAlign: "center",
            fontWeight: 700,
            fontSize: 15,
            color: "var(--save-deep)",
          }}
        >
          still <span className="money">{formatCents(countsTotal(counts))}</span> ✓
        </p>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Preset label="Fewest pieces" on={is(fewest)} onClick={() => setCounts(fewest)} />
        {/* Allowance is handed over in ones, so "where are my dollar bills?"
            is one tap rather than a drag. */}
        <Preset label="All in ones" on={is(ones)} onClick={() => setCounts(ones)} />
      </div>
    </div>
  );
}

function Preset({
  label,
  on,
  onClick,
}: {
  label: string;
  on: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      style={{
        minHeight: 44,
        padding: "0 14px",
        fontSize: 14,
        fontWeight: 600,
        background: on ? "var(--ink)" : "var(--paper)",
        color: on ? "var(--paper)" : "var(--ink)",
        borderColor: "var(--ink)",
      }}
    >
      {label}
    </button>
  );
}
